import useOnScreen from "hooks/useOnScreen";
import { useRef, useState, type ReactNode, type RefObject } from "react";
import { Badge } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import type { Instance } from "react-table-6";
import { Wrap } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import type { CustomFilter } from "../../../hooks/useTableSettings";
import FilterForm, { getFormatFilterOptions } from "./FilterForm";
import type { SelectorColumn } from "../../../hooks/useColumnSelector";
import { FilterColumnSelector } from "./FilterColumnSelector";
import { DialogTrigger } from "react-aria-components";
import { formatCurrency, formatNumber, formatPercent } from "../../../utils/formatNumber";
import { useAppSelector } from "../../../redux/hooks";
import type { RowData, Table } from "@tanstack/react-table";
import { resolveLabel } from "../../../components/table/utils";
import { z } from "zod";
import moment from "moment";

const formatToInitialValueMap = {
  currency: "0",
  number: "0",
  percent: "0",
  text: "",
  date: [moment().subtract(1, "month").toISOString(), moment().toISOString()],
  select: ["all"],
  "multi-select": ["all"],
  toggle: "on",
};

const formatFilterValue = ({
  value,
  format,
  selectOptions = [],
  marketPlace,
}: {
  value: unknown;
  format?: string;
  selectOptions?: { value: unknown; label: string }[];
  marketPlace: string;
}): ReactNode => {
  // If input can't be parsed into number format, return blank string to avoid NaNs from rendering
  if (["currency", "number", "percent"].includes(format!)) {
    try {
      parseFloat(value as string);
    } catch {
      return "";
    }
  }

  switch (format) {
    case "select":
      return Array.isArray(value) ?
          selectOptions.filter((o) => value.includes(o.value)).map((selected) => selected.label)
        : selectOptions.find((option) => option.value === value)?.label;
    case "currency":
      return formatCurrency(value as number, marketPlace, false, false, true);
    case "number":
      return formatNumber(value as number);
    case "percent":
      return formatPercent(value as number, 2);
    case "date":
      return moment(value as string).format("MM-DD-YYYY");
    case "text":
    default:
      return value as ReactNode;
  }
};

const getOperatorDisplay = (op: string) => {
  if (op === "anyof") {
    return "⊃";
  }
  if (op === "allof") {
    return "=";
  }

  return op;
};

interface ColumnOption {
  Header?: string | null;
  format?: string;
  id?: string;
  onValue?: string;
  offValue?: string;
  accessorId?: string;
  label?: string;
  filterType?: string;
  override?: boolean;
  defaultValue?: unknown;
  selectOptions?: { label: string; value: unknown }[];
  filterFunction?: (row: unknown, value: unknown) => boolean;
}

interface Props {
  tableRef?: RefObject<Instance<unknown>>;
  customFilters: CustomFilter[];
  setCustomFilters?: (updatedFilters: CustomFilter[]) => void;
  additionalFilterOptions: ColumnOption[];
  excludedFilterColumns?: string[];
  initialFilters?: CustomFilter[];
  tableId: string;
}

export const RT6FilterWidget = ({
  tableRef,
  customFilters,
  setCustomFilters,
  additionalFilterOptions = [],
  excludedFilterColumns = [],
}: Props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [showForm, setShowForm] = useState<string | null>(null);
  const widgetVisiblityRef = useRef<HTMLDivElement>(null);

  const columnOptions =
    (tableRef?.current?.props.columns as SelectorColumn[] | undefined)?.map((col): ColumnOption => {
      const { Header, format, id, onValue, offValue, accessorId } = col;

      return {
        Header,
        format,
        id,
        onValue,
        offValue,
        accessorId,
      };
    }) ?? [];

  const filterables = [
    ...additionalFilterOptions,
    ...columnOptions
      .filter((c) => ![...excludedFilterColumns, "isExpanded"].includes(c.id!))
      .filter(
        (option) => option.format && !customFilters.map((selectedFilter) => selectedFilter.id).includes(option.id!),
      ),
  ];

  // Below resolves issues where tables hidden within closed accordion doesn't reliably create a new table ref
  // in time to load column filter options
  useOnScreen(widgetVisiblityRef);

  return (
    <Wrap ref={widgetVisiblityRef} gap="xsmall" fontSize="medium">
      {customFilters.map((filter, index) => {
        const { id, label, format, operator, value, min, max, selectOptions } = filter;

        const formattedValue = formatFilterValue({ value, format, selectOptions, marketPlace });
        return (
          <DialogTrigger key={id} isOpen={showForm === id} onOpenChange={(isOpen) => setShowForm(isOpen ? id : null)}>
            <Button variant={"contentOnly"}>
              <Badge
                className="filter-badge bg-light border border-muted text-muted py-2 px-4 fs-5 p-relative"
                pill
                onClick={() => setShowForm(id)}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {label}
                {operator === "-" ?
                  <span className="ps-1 fs-6">
                    {formatFilterValue({ value: min, format, marketPlace }) ?? ""}
                    {` - `}
                    {formatFilterValue({ value: max, format, marketPlace }) ?? ""}
                  </span>
                : <>
                    <span className="ps-1 fs-6">{getOperatorDisplay(operator)} </span>
                    <span className="fs-6">
                      {Array.isArray(formattedValue) ?
                        <span>
                          {formattedValue[0] as ReactNode}
                          {formattedValue.length > 1 && <> +{formattedValue.length - 1}</>}
                        </span>
                      : (formatFilterValue({ value, format, selectOptions, marketPlace }) ?? "")}
                    </span>
                  </>
                }
                <FaChevronDown className="ps-3 fs-2" />
              </Badge>
            </Button>

            <FilterForm
              format={filter.format}
              title={filter.label}
              onApply={(filter) => {
                const updatedFilters = customFilters.map((cf) => {
                  if (cf.id === id) {
                    const nextFilter = { ...cf };
                    nextFilter.operator = filter.op;
                    if (filter.op === "-") {
                      nextFilter.min = filter.value[0];
                      nextFilter.max = filter.value[1];
                    } else {
                      nextFilter.value = filter.value;
                    }
                    return nextFilter;
                  }
                  return cf;
                });
                setCustomFilters?.(updatedFilters);
              }}
              onPressRemove={() => {
                const updatedFilters = customFilters.filter((f, filterIndex) => filterIndex !== index);
                setCustomFilters?.(updatedFilters);
              }}
              initialValues={{
                op: filter.operator,
                value: filter.operator === "-" ? [filter.min, filter.max] : filter.value,
              }}
              selectOptions={filter.selectOptions}
            />
          </DialogTrigger>
        );
      })}

      <FilterColumnSelector
        filterOptions={filterables.map((option) => ({
          id: option.id!,
          label: option.Header ?? option.label ?? option.id!,
        }))}
        onSelect={(id) => {
          const selectedOption = filterables.find((option) => option.id === id);
          if (!selectedOption) {
            return;
          }
          setCustomFilters?.([
            ...customFilters,
            {
              id: selectedOption.id!,
              label: selectedOption.Header ?? selectedOption.label,
              format: selectedOption.filterType ?? selectedOption.format,
              operator: selectedOption.override ? "=" : ">",
              value: selectedOption.defaultValue ?? null,
              overrideAccessorId: selectedOption.accessorId ?? selectedOption.id,
              onValue: selectedOption.onValue,
              offValue: selectedOption.offValue,
              selectOptions: selectedOption.selectOptions ?? [],
              override: selectedOption.override ?? false,
              filterFunction: selectedOption.filterFunction,
            },
          ]);
          setShowForm(id);
        }}
      />

      {customFilters.length ?
        <Button variant={"contentOnly"} onPress={() => setCustomFilters?.([])}>
          <FaTrash />
          Clear filter{customFilters.length > 1 ? "s" : ""}
        </Button>
      : null}
    </Wrap>
  );
};

export const RT8FilterWidget = <TData extends RowData>({ table }: { table: Table<TData> }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [showForm, setShowForm] = useState<string | null>(null);

  const filterOptions = table
    .getAllColumns()
    .filter((col) => col.getCanFilter() && !col.getIsFiltered())
    .map((col) => ({
      id: col.id,
      label: resolveLabel(col, "columnFilterOptions"),
    }));
  const filters = table.getState().columnFilters;

  return (
    <Wrap gap="xsmall">
      {filters.map((filter) => {
        const parsedFilter = z
          .object({
            id: z.string(),
            value: z
              .object({
                op: z.string(),
                value: z.unknown(),
              })
              .transform(({ op, value }) => ({ op, value })),
          })

          .parse(filter);

        const column = table.getColumn(filter.id);
        if (!column) {
          return null;
        }
        const label = resolveLabel(column, "columnFilterOptions");
        const format = column.columnDef.meta?.columnFilterOptions?.format ?? "text";
        const selectOptions = table.options.meta?.columnFilterSelectOptions?.[filter.id] ?? [];
        const operator = parsedFilter.value.op;
        const value = parsedFilter.value.value;

        const formattedValue = formatFilterValue({ value, format, selectOptions, marketPlace });

        return (
          <DialogTrigger
            key={filter.id}
            isOpen={showForm === filter.id}
            onOpenChange={(isOpen) => setShowForm(isOpen ? filter.id : null)}
          >
            <Button variant={"contentOnly"}>
              <Badge
                className="filter-badge bg-light border border-muted text-muted py-2 px-4 fs-5 p-relative"
                pill
                onClick={() => setShowForm(filter.id)}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {label}
                {operator === "-" ?
                  <span className="ps-1 fs-6">
                    {formatFilterValue({ value: (parsedFilter.value.value as string[])[0], format, marketPlace }) ?? ""}
                    -
                    {formatFilterValue({ value: (parsedFilter.value.value as string[])[1], format, marketPlace }) ?? ""}
                  </span>
                : <>
                    <span className="ps-1 fs-6">{getOperatorDisplay(operator)} </span>
                    <span className="fs-6">
                      {Array.isArray(formattedValue) ?
                        <span>
                          {formattedValue[0] as ReactNode}
                          {formattedValue.length > 1 && <> +{formattedValue.length - 1}</>}
                        </span>
                      : (formatFilterValue({ value, format, selectOptions, marketPlace }) ?? "")}
                    </span>
                  </>
                }
                <FaChevronDown className="ps-3 fs-2" />
              </Badge>
            </Button>

            <FilterForm
              format={format}
              title={label}
              onApply={(filterValue) => {
                column.setFilterValue(filterValue);
              }}
              onPressRemove={() => {
                column.setFilterValue(undefined);
              }}
              initialValues={parsedFilter.value}
              selectOptions={selectOptions}
            />
          </DialogTrigger>
        );
      })}

      <FilterColumnSelector
        filterOptions={filterOptions}
        onSelect={(id) => {
          const column = table.getColumn(id);
          const format = column?.columnDef.meta?.columnFilterOptions?.format ?? "text";
          const op = getFormatFilterOptions(format)[0]?.value ?? "=";
          const initialValue = formatToInitialValueMap[format];
          column?.setFilterValue({ op, value: initialValue });

          setShowForm(id);
        }}
      />

      {filters.length ?
        <Button
          variant={"contentOnly"}
          onPress={() => table.resetColumnFilters(true)}
          color={"text.secondary"}
          fontSize={"medium"}
        >
          <FaTrash />
          Clear filter{filters.length > 1 ? "s" : ""}
        </Button>
      : null}
    </Wrap>
  );
};
